import { FileWarning, RefreshCw } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { InlineError } from '@/components/ui/StatePanel';
import { useBackState } from '@/lib/back-nav';

import { overdueDaysLabel, stageLabel } from '../presentation';
import { useActiveEscalations } from '../use-notification-settings';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

/** Tarjeta roja "Escalamiento activo" del mockup: etapas escaladas ahora, con enlace a
 *  la ficha. Sin escalados o sin permiso de consulta del panel no se muestra. Si la consulta
 *  falla no se sabe si hay escalados: la tarjeta lo dice y ofrece reintentar (UX-ERR-01). */
export function ActiveEscalations({
  companyId,
  enabled,
}: {
  companyId: string;
  enabled: boolean;
}): ReactElement | null {
  const query = useActiveEscalations({ companyId, enabled });
  const fromHere = useBackState('Notificaciones');
  const data = query.data;
  // Sin escalados a la vista (o sin datos), una falla impide saber si ahora hay: se avisa.
  if (enabled && query.isError && (data === undefined || data.items.length === 0)) {
    return (
      <section className="card rise d3" aria-labelledby="active-escalations-title">
        <div className="ch">
          <h3 id="active-escalations-title">Escalamiento activo</h3>
        </div>
        <InlineError
          icon={<FileWarning />}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        >
          No se pudo consultar si hay escalamientos activos. {errorAdvice(query.error)}
        </InlineError>
      </section>
    );
  }
  if (!enabled || !data || data.items.length === 0) {
    return null;
  }
  const more = data.total - data.items.length;

  return (
    <div className="card rise d3 esc-alert">
      <div className="cb">
        <span className="esc-bang" aria-hidden="true">
          !
        </span>
        <div className="esc-alert-body">
          <h3>Escalamiento activo</h3>
          <ul>
            {data.items.map((i) => (
              <li key={`${i.entry.id}-${i.stage ?? ''}`}>
                <Link to={`/documentos/${i.entry.id}`} state={fromHere} className="code">
                  {i.entry.code}
                </Link>{' '}
                {stageLabel(i.stage)} · {overdueDaysLabel(i.daysLeft)}
                {i.escalatedTo.length > 0 &&
                  ` · escalado a ${i.escalatedTo.map((p) => p.name).join(', ')}`}
              </li>
            ))}
          </ul>
          {more > 0 && <p className="esc-more">{`y ${more} más escalados`}</p>}
          {/* Una recarga que falla conserva los escalados que ya se veían y lo avisa. */}
          {query.isError && (
            <InlineError
              icon={<RefreshCw size={16} />}
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
              dense
            >
              No se pudieron actualizar los escalamientos. Se muestran los anteriores.
              {refreshAdvice(query.error)}
            </InlineError>
          )}
        </div>
      </div>
    </div>
  );
}
